import { useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch, apiImageUrl, getToken, STREAM_API } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';
import ReportDataMenu from '../../components/ReportDataMenu';

// ── Count Report (own menu) ───────────────────────────────────────────────────
// Standalone report for the line-crossing Object Counting detector: live per-camera
// tally, a live annotated preview so you can watch counts tick up in real time, and
// the full crossing-event log with CSV export. Configuration lives separately on the
// Object Intelligence page — this screen is read-only monitoring.

// Local midnight as a datetime-local string — the default crossing window ("today").
const todayMidnight = () => {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T00:00`;
};

export default function IndustrialCountReportPage() {
  const [cameras, setCameras] = useState([]);
  const [summary, setSummary] = useState([]);
  const [events, setEvents]   = useState([]);
  const [scope, setScope]     = useState(null);        // camera shown in live preview
  const [live, setLive]       = useState(true);
  const [imgUrl, setImgUrl]   = useState(null);
  const [allCams, setAllCams] = useState(false);       // report follows the live camera unless opted out
  const [hideTest, setHideTest] = useState(true);      // hide 🧪 Test-button events by default
  const [lineFilter, setLineFilter] = useState('');    // filter crossings to one counting line ('' = all)
  const [dirFilter, setDirFilter]   = useState('');    // filter crossings by direction ('' = all | 'in' | 'out')
  const [fullscreen, setFullscreen] = useState(false); // full-screen live movement + counts view
  const [gridView, setGridView] = useState(true);      // land on every counting camera at once
  const [filtersOpen, setFiltersOpen] = useState(false); // crossing-log filter popover
  const [token, setToken]     = useState(null);        // for the MJPEG stream tiles in grid view
  const [snapUrl, setSnapUrl] = useState(null);        // crossing snapshot shown in the lightbox
  const [snapMeta, setSnapMeta] = useState(null);      // the event behind the shown snapshot (for the caption)
  // Default the crossing log to TODAY (from local midnight, open-ended so new crossings
  // keep appearing). Clear window → back to latest-300 live tail.
  const [from, setFrom] = useState(todayMidnight);
  const [to, setTo]     = useState('');
  const prevUrl = useRef(null);

  // Cameras once; counts every 5s.
  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json())
      .then(d => { const l = d.cameras || []; setCameras(l); if (l.length) setScope(l[0].idx); })
      .catch(() => {});
    getToken().then(setToken).catch(() => {});
  }, []);

  const viewSnap = (e) => { setSnapMeta(e); apiImageUrl(`/ui/count/snapshot/${encodeURIComponent(e.snapshot)}`).then(setSnapUrl).catch(() => {}); };
  const closeSnap = () => { if (snapUrl) URL.revokeObjectURL(snapUrl); setSnapUrl(null); setSnapMeta(null); };

  // Event log follows the camera in the live view (so pathway shows pathway events,
  // not another camera's junk) unless "All cameras" is ticked.
  const loadCounts = useCallback(() => {
    apiFetch('/ui/count/summary').then(r => r.json()).then(d => setSummary(d.cameras || [])).catch(() => {});
    const p = [];
    if (!allCams && scope != null) p.push(`cam=${scope}`);
    if (from) p.push(`ts_from=${Math.floor(new Date(from).getTime() / 1000)}`);
    if (to)   p.push(`ts_to=${Math.floor(new Date(to).getTime() / 1000)}`);
    p.push(`limit=${(from || to) ? 5000 : 300}`);   // pull the whole window when one is set
    apiFetch(`/ui/count/events?${p.join('&')}`).then(r => r.json()).then(d => setEvents(d.events || [])).catch(() => {});
  }, [allCams, scope, from, to]);
  useEffect(() => { loadCounts(); const t = setInterval(loadCounts, 5000); return () => clearInterval(t); }, [loadCounts]);
  // Counting lines differ per camera → clear the line filter when the scope changes.
  useEffect(() => { setLineFilter(''); }, [scope, allCams]);

  // Live annotated preview — same server-drawn frame the config page uses (boxes +
  // the configured line + running tally burned in).
  useEffect(() => {
    if (scope == null) return;
    // Grid tiles use the cheap MJPEG stream; but full-screen always shows this annotated
    // frame live regardless of the focus/pause state, so force ticking when fullscreen.
    if (!fullscreen && (!live || gridView)) return;
    let alive = true;
    const tick = () => apiImageUrl(`/ui/cameras/${scope}/annotated?t=${Date.now()}`).then(u => {
      if (!alive) { URL.revokeObjectURL(u); return; }
      setImgUrl(u); if (prevUrl.current) URL.revokeObjectURL(prevUrl.current); prevUrl.current = u;
    }).catch(() => {});
    tick(); const iv = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(iv); if (prevUrl.current) { URL.revokeObjectURL(prevUrl.current); prevUrl.current = null; } };
  }, [live, scope, gridView, fullscreen]);

  // Esc exits full-screen.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = e => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const fmt = (ts) => { const d = ts ? new Date(ts * 1000) : null; return d ? d.toLocaleString() : '—'; };
  const isTest = (e) => (e.object || '').toLowerCase() === 'test' || e.track_id === 'TEST';
  const lineOf = (e) => e.line || e.object || '—';
  // Footfall lines have their own report (customers in/out, occupancy), so they are
  // kept out of this one — otherwise the same crossings appear on two screens with
  // two different meanings. Keyed by "camIdx\nlineName" since line names repeat.
  const footfallKeys = new Set();
  cameras.forEach(c => ((c.detector_config?.object_count?.lines) || []).forEach(L => {
    if ((L.purpose || 'object') === 'footfall') footfallKeys.add(`${c.idx}\n${L.name}`);
  }));
  const isFootfall = (e) => footfallKeys.has(`${e.cam_idx}\n${lineOf(e)}`);
  // 'reset' rows are occupancy markers for the footfall page, not crossings.
  const realEvents = events.filter(e => e.direction !== 'reset' && !isFootfall(e));
  // Distinct counting lines present in the loaded crossings (for the line-filter picker).
  const lineList = [...new Set((hideTest ? realEvents.filter(e => !isTest(e)) : realEvents).map(lineOf))].sort();
  const shownEvents = realEvents.filter(e =>
    (!hideTest || !isTest(e)) &&
    (!lineFilter || lineOf(e) === lineFilter) &&
    (!dirFilter || e.direction === dirFilter));
  // Totals for the shown/filtered crossings, counted BY DIRECTION (how many went in vs out).
  const totalIn  = shownEvents.reduce((n, e) => n + (e.direction === 'in'  ? 1 : 0), 0);
  const totalOut = shownEvents.reduce((n, e) => n + (e.direction === 'out' ? 1 : 0), 0);
  // Cumulative crossings for the SELECTED window — grouped by counting line, derived
  // from the date-filtered events (NOT the since-start server counters). An empty day
  // reads 0, and only object classes that actually crossed in the window appear, so the
  // panel always matches the current counting config / the data available that day.
  const windowLines = Object.values(shownEvents.reduce((m, e) => {
    const key = e.line || e.object || '—';
    if (!m[key]) m[key] = { line: key, in: 0, out: 0 };
    if (e.direction === 'in')       m[key].in++;
    else if (e.direction === 'out') m[key].out++;
    return m;
  }, {}));
  const scopeName = cameras.find(c => c.idx === scope)?.name || (scope != null ? `Camera ${scope}` : '');
  // Live tallies minus the footfall lines, for the same reason as above.
  const visSummary = summary.filter(s => !footfallKeys.has(`${s.cam_idx}\n${s.line}`));
  // Cameras with object counting configured (fallback: all) — the grid tiles these.
  // A camera whose only lines are footfall belongs to the Footfall Report, not here.
  const hasCounting = c => {
    const oc = c.detector_config?.object_count;
    if (!oc) return false;
    const ls = oc.lines || [];
    return ls.length ? ls.some(L => (L.purpose || 'object') !== 'footfall') : !!oc.line;
  };
  const counting     = cameras.filter(hasCounting);
  const others       = cameras.filter(c => !hasCounting(c));
  const countingCams = counting.length ? counting : cameras;

  // Camera picker — the single place a camera is chosen (it replaced a row of one
  // pill per camera·line). Each option carries that camera's live tally, so the
  // counts stay visible without a second strip of numbers competing with the
  // window totals in the panel below. Counting cameras are grouped first.
  const camLabel = (c) => {
    const ls   = visSummary.filter(s => s.cam_idx === c.idx);
    const name = c.name || `Camera ${c.idx}`;
    if (ls.length === 1) return `${name} · IN ${ls[0].in} / OUT ${ls[0].out}`;
    if (ls.length > 1)   return `${name} · ${ls.length} lines`;
    return name;
  };
  // Rendered by CALLING this (not as <CamSelect/>) so the 5s count refresh re-labels
  // the options in place instead of remounting the select under an open dropdown.
  const camSelect = (style) => (
    <select value={scope ?? ''} onChange={e => setScope(Number(e.target.value))}
      title="Camera shown in the live view" style={{ ...sel, ...(style || {}) }}>
      {counting.length === 0
        ? cameras.map(c => <option key={c.idx} value={c.idx}>🎥 {camLabel(c)}</option>)
        : <>
            <optgroup label="Counting cameras">
              {counting.map(c => <option key={c.idx} value={c.idx}>🎥 {camLabel(c)}</option>)}
            </optgroup>
            {others.length > 0 && (
              <optgroup label="Other cameras">
                {others.map(c => <option key={c.idx} value={c.idx}>🎥 {camLabel(c)}</option>)}
              </optgroup>
            )}
          </>}
    </select>
  );

  // One-line summary of the active filters, shown on the Filters button so a hidden
  // filter can never silently change what the table means.
  const filterChip = [
    (from === todayMidnight() && !to) ? 'today' : (from || to) ? 'window' : 'all time',
    lineFilter || null,
    dirFilter ? dirFilter.toUpperCase() : null,
    hideTest ? null : '+test',
  ].filter(Boolean).join(' · ');

  const exportCsv = () => {
    const rows = [['time', 'camera', 'line', 'object', 'direction', 'in', 'out', 'snapshot'],
      ...shownEvents.map(e => [fmt(e.ts), e.cam_name, e.line || e.object, e.object, e.direction, e.in, e.out, e.snapshot || ''])];
    const csv = rows.map(r => r.map(x => `"${String(x ?? '')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'count_report.csv'; a.click();
  };

  return (
    <IndustrialLayout eyebrow="Object intelligence" title="Object Intelligence Report"
      subtitle={`${cameras.length} CAMERAS · ${shownEvents.length} CROSSINGS SHOWN`}>

      {/* Live view (left) + count section & crossing log (right) */}
      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span className="ind-eye">Live view</span>
          <button className={`ind-pill${!gridView ? ' on' : ''}`} onClick={() => setGridView(false)}>◻ Focus</button>
          <button className={`ind-pill${gridView ? ' on' : ''}`} onClick={() => setGridView(true)}>▦ All cameras</button>
          {!gridView && <>
            {camSelect()}
            <button className={`ind-pill${live ? ' on' : ''}`} onClick={() => setLive(l => !l)}>{live ? '⏸ Pause' : '▶ Resume'}</button>
            <button className="ind-pill" onClick={() => setFullscreen(true)} title="Full-screen live movement + counts">⛶ Full screen</button>
          </>}
        </div>

        {gridView ? (
          <div className="ind-grid">
            {countingCams.map(c => (
              <CountTile key={c.idx} cam={c} token={token}
                lines={visSummary.filter(s => s.cam_idx === c.idx)}
                onClick={() => { setScope(c.idx); setGridView(false); }} />
            ))}
            {countingCams.length === 0 && <div className="ind-empty">No counting cameras configured.</div>}
          </div>
        ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* LEFT — live annotated preview (given the larger share so objects read clearly) */}
          <div style={{ flex: '2 1 620px', minWidth: 340 }}>
            <div style={{ position: 'relative', width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ind-line)' }}>
              {imgUrl
                ? <img src={imgUrl} alt="" style={{ width: '100%', display: 'block' }} />
                : <div className="ind-empty" style={{ padding: 40 }}>{live ? 'Loading live frame…' : 'Preview paused.'}</div>}
            </div>
            <p style={{ fontSize: 12, color: 'var(--ind-tx3)', marginTop: 8 }}>
              Boxes, the centre dot and every counting line (yellow, named) are drawn by the server.
              Configure the counting lines on the <b>Object Intelligence</b> page.
            </p>
          </div>

          {/* RIGHT — count section + crossing log for the selected camera */}
          <div style={{ flex: '1 1 300px', minWidth: 280 }}>
            <div style={{ fontSize: 11, color: 'var(--ind-tx3)', letterSpacing: '.06em', marginBottom: 6 }}>
              {(allCams ? 'ALL CAMERAS' : (scopeName || 'THIS CAMERA')).toUpperCase()} — CROSSINGS · SELECTED WINDOW
            </div>
            {windowLines.length === 0 ? (
              <div className="ind-empty" style={{ marginBottom: 12 }}>No crossings in the selected window.</div>
            ) : windowLines.length === 1 ? (
              <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                <Stat label={`IN · ${windowLines[0].line}`}  val={windowLines[0].in}  color="var(--ind-ok)" />
                <Stat label="OUT" val={windowLines[0].out} color="var(--ind-accent)" />
                <Stat label="NET" val={windowLines[0].in - windowLines[0].out} color="var(--ind-tx)" />
              </div>
            ) : (
              // Multiple object types with crossings this window → one card per line, never summed.
              <div style={{ marginBottom: 8, border: '1px solid var(--ind-line)', borderRadius: 8, overflow: 'hidden' }}>
                {windowLines.map((s, i) => (
                  <div key={`${s.line}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 8, fontSize: 13, padding: '9px 12px', borderTop: i ? '1px solid var(--ind-line)' : 'none' }}>
                    <span style={{ color: 'var(--ind-tx2)', fontWeight: 600 }}>{s.line}</span>
                    <span style={{ fontFamily: 'var(--ind-mono)' }}>
                      <b style={{ color: 'var(--ind-ok)' }}>IN {s.in}</b> ·{' '}
                      <b style={{ color: 'var(--ind-accent)' }}>OUT {s.out}</b> ·{' '}
                      NET {s.in - s.out}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '0 0 12px' }}>
              Totals for the selected date window (from the crossing log below) — <b>not</b> people currently in frame.
            </p>
          </div>
        </div>
        )}
      </div>

      {/* Crossing log — its own full-width card. It was wedged into a 300px column
          beside the video with ten controls above it; the filters now live behind one
          button so the toolbar is three items and the table gets the whole width. */}
      <div className="ind-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span className="ind-eye">Crossings</span>
              <button className={`ind-pill${!allCams ? ' on' : ''}`} onClick={() => setAllCams(false)}>📷 This camera</button>
              <button className={`ind-pill${allCams ? ' on' : ''}`} onClick={() => setAllCams(true)}>🎥 All cameras</button>
              <div style={{ position: 'relative' }}>
                <button className={`ind-pill${filtersOpen ? ' on' : ''}`} onClick={() => setFiltersOpen(o => !o)}>
                  ⚙ Filters · {filterChip}
                </button>
                {filtersOpen && (
                  <div style={{ position: 'absolute', zIndex: 30, top: '110%', left: 0, minWidth: 260,
                    background: 'var(--ind-ink)', border: '1px solid var(--ind-line)', borderRadius: 10,
                    padding: 12, display: 'flex', flexDirection: 'column', gap: 10,
                    boxShadow: '0 12px 30px rgba(0,0,0,.45)' }}>
                    {lineList.length > 1 && (
                      <label style={{ fontSize: 12, color: 'var(--ind-tx2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        Line
                        <select value={lineFilter} onChange={e => setLineFilter(e.target.value)} style={{ ...sel, flex: 1 }}>
                          <option value="">All lines</option>
                          {lineList.map(ln => <option key={ln} value={ln}>{ln}</option>)}
                        </select>
                      </label>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className={`ind-pill${dirFilter === 'in' ? ' on' : ''}`}
                        onClick={() => setDirFilter(dirFilter === 'in' ? '' : 'in')}>➡ IN only</button>
                      <button className={`ind-pill${dirFilter === 'out' ? ' on' : ''}`}
                        onClick={() => setDirFilter(dirFilter === 'out' ? '' : 'out')}>⬅ OUT only</button>
                    </div>
                    <label style={{ fontSize: 12, color: 'var(--ind-tx2)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                      <input type="checkbox" checked={hideTest} onChange={e => setHideTest(e.target.checked)} /> Hide 🧪 test crossings
                    </label>
                    <label style={{ fontSize: 12, color: 'var(--ind-tx2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      From <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} style={{ ...sel, flex: 1 }} />
                    </label>
                    <label style={{ fontSize: 12, color: 'var(--ind-tx2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      To <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} style={{ ...sel, flex: 1 }} />
                    </label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(from || to) && <button className="ind-pill" onClick={() => { setFrom(''); setTo(''); }}>Clear window</button>}
                      <button className="ind-pill" style={{ marginLeft: 'auto' }} onClick={() => setFiltersOpen(false)}>Done</button>
                    </div>
                  </div>
                )}
              </div>
              <span style={{ marginLeft: 'auto' }} />
              {(() => {
                // This report's lines on the live camera: object/vehicle lines, never
                // footfall (that report resets its own).
                const c = cameras.find(x => x.idx === scope);
                const its = ((c?.detector_config?.object_count?.lines) || [])
                  .filter(L => (L.purpose || 'object') !== 'footfall').map(L => L.name).filter(Boolean);
                return its.length > 0 && <ReportDataMenu kind="count" cam={scope}
                  camName={c?.name || `Camera ${scope}`} items={its} onChanged={loadCounts} />;
              })()}
              <button className="ind-pill" onClick={exportCsv}>⭳ CSV</button>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto', border: '1px solid var(--ind-line)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ textAlign: 'left', color: 'var(--ind-tx3)' }}>
                  <th style={th}>Time</th>{allCams && <th style={th}>Camera</th>}<th style={th}>Line</th><th style={th}>Dir</th><th style={th}>IN</th><th style={th}>OUT</th><th style={th}>Evidence</th>
                </tr></thead>
                <tbody>
                  {shownEvents.map((e, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--ind-line)' }}>
                      <td style={td}>{fmt(e.ts)}</td>{allCams && <td style={td}>{e.cam_name}</td>}<td style={td}>{e.line || e.object}</td>
                      <td style={{ ...td, color: e.direction === 'in' ? 'var(--ind-ok)' : 'var(--ind-accent)' }}>{e.direction?.toUpperCase()}</td>
                      <td style={td}>{e.in}</td><td style={td}>{e.out}</td>
                      <td style={td}>{e.snapshot
                        ? <button className="ind-pill" style={{ padding: '2px 8px', color: e.direction === 'in' ? 'var(--ind-ok)' : 'var(--ind-accent)' }}
                            onClick={() => viewSnap(e)}>🔍 View {e.direction?.toUpperCase()}</button>
                        : <span style={{ color: 'var(--ind-tx3)' }}>—</span>}</td>
                    </tr>
                  ))}
                  {shownEvents.length === 0 && <tr><td style={td} colSpan={allCams ? 7 : 6}>
                    {allCams ? 'No crossing events yet.' : `No crossings for ${scopeName || 'this camera'} yet.`}
                  </td></tr>}
                </tbody>
                {shownEvents.length > 0 && (
                  <tfoot>
                    <tr style={{ position: 'sticky', bottom: 0, background: 'var(--ind-ink)',
                      borderTop: '2px solid var(--ind-line)', fontWeight: 700 }}>
                      <td style={tf} colSpan={allCams ? 3 : 2}>TOTALS · {shownEvents.length} crossings</td>
                      <td style={tf}>—</td>
                      <td style={{ ...tf, color: 'var(--ind-ok)' }}>{totalIn} in</td>
                      <td style={{ ...tf, color: 'var(--ind-accent)' }}>{totalOut} out</td>
                      <td style={tf} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
      </div>

      {/* Full-screen live movement + counts — big, glanceable IN/OUT for a wall display */}
      {fullscreen && (
        <div style={{ position: 'fixed', inset: 0, background: '#0a0e14', zIndex: 1200, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #1c2733', flexWrap: 'wrap' }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 18, letterSpacing: '.02em' }}>{(scopeName || 'Camera').toUpperCase()}</span>
            {camSelect({ background: '#0f1620', color: '#e5e7eb', borderColor: '#243040' })}
            <span style={{ color: '#22c55e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>● LIVE</span>
            <button className="ind-pill" onClick={() => setFullscreen(false)}
              style={{ marginLeft: 'auto', color: '#fff', background: '#0f1620', borderColor: '#334155' }}>✕ Exit full screen (Esc)</button>
          </div>
          <div style={{ flex: 1, display: 'flex', minHeight: 0, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 480px', display: 'grid', placeItems: 'center', padding: 12, minWidth: 0 }}>
              {imgUrl
                ? <img src={imgUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
                : <div style={{ color: '#8b95a5' }}>Loading live frame…</div>}
            </div>
            <div style={{ flex: '0 0 340px', borderLeft: '1px solid #1c2733', padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ color: '#8b95a5', fontSize: 12, letterSpacing: '.1em' }}>
                MOVEMENT — {from || to ? 'SELECTED WINDOW' : 'ALL TIME'}
              </div>
              {windowLines.length === 0
                ? <div style={{ color: '#667085' }}>No crossings in the selected window.</div>
                : windowLines.map((s, i) => (
                  <div key={i} style={{ border: '1px solid #1c2733', borderRadius: 12, padding: 14, background: '#0f1620' }}>
                    <div style={{ color: '#cbd5e1', fontWeight: 700, marginBottom: 10, fontSize: 15 }}>{s.line}</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <FSStat label="IN"  val={s.in}  color="#22c55e" />
                      <FSStat label="OUT" val={s.out} color="#f59e0b" />
                      <FSStat label="NET" val={s.in - s.out} color="#e5e7eb" />
                    </div>
                  </div>
                ))}
              <div style={{ color: '#667085', fontSize: 11, marginTop: 'auto' }}>
                Counts honour the date{lineFilter ? ' + line' : ''}{dirFilter ? ' + direction' : ''} filter from the report.
                Boxes are labelled with their class (e.g. box) and the counting line is drawn in yellow.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Crossing evidence lightbox */}
      {snapUrl && (
        <div onClick={closeSnap}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.82)', zIndex: 1000,
            display: 'grid', placeItems: 'center', cursor: 'zoom-out', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', maxWidth: '92vw', maxHeight: '92vh' }}>
            {snapMeta && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', color: '#fff', fontSize: 14, fontFamily: 'var(--ind-mono)' }}>
                <span style={{ fontWeight: 700, color: snapMeta.direction === 'in' ? '#22c55e' : '#f59e0b' }}>
                  {snapMeta.direction?.toUpperCase()}
                </span>
                <span>· {snapMeta.line || snapMeta.object}</span>
                <span>· {snapMeta.cam_name}</span>
                <span>· {fmt(snapMeta.ts)}</span>
                <button className="ind-pill" onClick={closeSnap} style={{ marginLeft: 8 }}>✕ Close</button>
              </div>
            )}
            <img src={snapUrl} alt="crossing evidence"
              style={{ maxWidth: '92vw', maxHeight: '82vh', borderRadius: 8, border: '2px solid #fff' }} />
          </div>
        </div>
      )}
    </IndustrialLayout>
  );
}

// Grid tile — the cheap MJPEG detection stream (no extra inference) with the camera's
// live per-line counts overlaid. Click to jump into focus view for that camera.
function CountTile({ cam, token, lines, onClick }) {
  const [failed, setFailed] = useState(false);
  const idx = cam.idx;
  return (
    <div className="ind-cell" onClick={onClick} style={{ cursor: 'zoom-in', position: 'relative' }} title="Click to focus">
      {token && !failed
        ? <img src={`${STREAM_API}/cameras/${idx}/stream?token=${token}`} onError={() => setFailed(true)}
            alt="" style={{ width: '100%', display: 'block' }} />
        : <div className="ph">📹</div>}
      <span className="lbl">{(cam.name || `Cam ${idx}`).toUpperCase()}</span>
      <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {lines.map((s, i) => (
          <span key={`${s.line}-${i}`} style={{ fontSize: 11, fontFamily: 'var(--ind-mono)',
            background: 'rgba(0,0,0,.65)', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>
            {s.line}: <b style={{ color: '#22c55e' }}>IN {s.in}</b> / <b style={{ color: '#f59e0b' }}>OUT {s.out}</b>
          </span>
        ))}
        {lines.length === 0 && (
          <span style={{ fontSize: 11, background: 'rgba(0,0,0,.65)', padding: '2px 6px', borderRadius: 4, color: 'var(--ind-tx3)' }}>
            no counts yet
          </span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, val, color }) {
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: '10px 6px', borderRadius: 8, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
      <div style={{ fontSize: 26, fontWeight: 700, fontFamily: 'var(--ind-mono)', color, lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--ind-tx3)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

// Big-number stat for the full-screen wall view.
function FSStat({ label, val, color }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontSize: 44, fontWeight: 800, fontFamily: 'var(--ind-mono)', color, lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: 11, letterSpacing: '.14em', color: '#8b95a5', marginTop: 6 }}>{label}</div>
    </div>
  );
}

const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const th  = { padding: '6px 10px', fontWeight: 600 };
const td  = { padding: '6px 10px', fontFamily: 'var(--ind-mono)' };
const tf  = { padding: '8px 10px', fontFamily: 'var(--ind-mono)', whiteSpace: 'nowrap' };
